import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, orderBy, setDoc, getDoc, getDocs } from 'firebase/firestore';

const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const [agents, setAgents] = useState([]);
  const [games, setGames] = useState([]);
  const [transactions, setTransactions] = useState({});
  const [auditLogs, setAuditLogs] = useState([]); 
  const [agentTransactions, setAgentTransactions] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Credit 관련 상태
  const [credits, setCredits] = useState([]);
  const [creditVault, setCreditVault] = useState({ balance: 50000000 }); 

  // Cage 및 Handover 관련 상태
  const [cageLogs, setCageLogs] = useState([]);
  const [handoverLogs, setHandoverLogs] = useState([]);

  // Telegram 관련 상태
  const [telegramLogs, setTelegramLogs] = useState([]); 
  const [smsLogs, setSmsLogs] = useState([]); // 하위 호환성 유지
  const [smsTemplates, setSmsTemplates] = useState(() => { 
      const saved = localStorage.getItem('junket_sms_templates');
      return saved ? JSON.parse(saved) : [
          { id: 1, title: '공지', content: '[공지] 시스템 점검 안내입니다.' },
          { id: 2, title: '정산', content: '[알림] 정산이 완료되었습니다.' }
      ];
  });

  const [funds, setFunds] = useState(() => {
      const saved = localStorage.getItem('junket_funds');
      return saved ? JSON.parse(saved) : { cash: 0, cashChips: 0, rollingChips: 0 };
  });
  const [globalCurrency, setGlobalCurrency] = useState(() => localStorage.getItem('junket_currency') || 'PHP');
  const [staffList, setStaffList] = useState(() => {
      const saved = localStorage.getItem('junket_staff');
      return saved ? JSON.parse(saved) : [
          { id: 1, code: 'MANAGER01', name: 'General Manager', role: 'Manager', password: '123' }
      ];
  });
  const [permissions, setPermissions] = useState(() => {
      const saved = localStorage.getItem('junket_permissions');
      return saved ? JSON.parse(saved) : {
          'ShowCash': { 'Senior Manager': true, Manager: true, Staff: false, Group: false, Agent: false },
          'CreateAgent': { 'Senior Manager': true, Manager: true, Staff: true, Group: false, Agent: false },
          'CreateGame': { 'Senior Manager': true, Manager: true, Staff: true, Group: false, Agent: false },
          'Settle': { 'Senior Manager': true, Manager: true, Staff: true, Group: false, Agent: false },
          'CreditApprove': { 'Senior Manager': true, Manager: true, Staff: false, Group: false, Agent: false },
      };
  });

  // 시스템 설정 확장 (로고, 프린터, 디바이스)
  const [sysConfig, setSysConfig] = useState(() => {
      const saved = localStorage.getItem('junket_sys_config');
      const parsed = saved ? JSON.parse(saved) : {};
      
      return {
          telegramBotToken: parsed.telegramBotToken || '',
          companyName: parsed.companyName || 'My Casino Junket',
          logoData: parsed.logoData || '', // Base64 Image String
          
          settlementTemplate: parsed.settlementTemplate || {
              headerColor: '#0f172a',
              footerText: 'Thank you for your business.',
              showLogo: true,
              terms: 'Settlement is final upon signature.'
          },
          slipTemplate: parsed.slipTemplate || {
              headerColor: '#2563eb',
              footerText: 'Please keep this slip for your records.',
              showLogo: true
          },
          printerSettings: parsed.printerSettings || {
              settlement: { paperSize: 'A4', orientation: 'portrait', margin: '10mm' },
              slip: { paperSize: 'A5', orientation: 'landscape', margin: '5mm' } 
          },
          bluetoothDevice: parsed.bluetoothDevice || { name: null, id: null }
      };
  });

  // 로컬 스토리지 저장
  useEffect(() => { localStorage.setItem('junket_sms_templates', JSON.stringify(smsTemplates)); }, [smsTemplates]);
  useEffect(() => { localStorage.setItem('junket_funds', JSON.stringify(funds)); }, [funds]);
  useEffect(() => { localStorage.setItem('junket_staff', JSON.stringify(staffList)); }, [staffList]);
  useEffect(() => { localStorage.setItem('junket_permissions', JSON.stringify(permissions)); }, [permissions]);
  useEffect(() => { localStorage.setItem('junket_currency', globalCurrency); }, [globalCurrency]);
  useEffect(() => { localStorage.setItem('junket_sys_config', JSON.stringify(sysConfig)); }, [sysConfig]);

  // 데이터 동기화 리스너
  useEffect(() => {
    const qAgents = query(collection(db, "agents"), orderBy("id", "asc"));
    onSnapshot(qAgents, (snap) => setAgents(snap.docs.map(d => ({ ...d.data(), docId: d.id }))));

    const qGames = query(collection(db, "games"), orderBy("startedAt", "desc"));
    onSnapshot(qGames, (snap) => setGames(snap.docs.map(d => ({ ...d.data(), docId: d.id }))));

    const qTxs = query(collection(db, "transactions"), orderBy("date", "desc"));
    onSnapshot(qTxs, (snap) => {
      const grouped = {};
      snap.docs.forEach(d => {
        const t = d.data();
        if (!grouped[t.gameId]) grouped[t.gameId] = [];
        grouped[t.gameId].push(t);
      });
      setTransactions(grouped);
    });

    const qAudit = query(collection(db, "audit_logs"), orderBy("date", "desc"));
    onSnapshot(qAudit, (snap) => setAuditLogs(snap.docs.map(d => d.data())));

    const qAgentTxs = query(collection(db, "agent_transactions"), orderBy("date", "desc"));
    onSnapshot(qAgentTxs, (snap) => {
      setAgentTransactions(snap.docs.map(d => d.data()));
      setLoading(false);
    });

    const qLoans = query(collection(db, "loans"), orderBy("createdAt", "desc"));
    onSnapshot(qLoans, (snap) => setCredits(snap.docs.map(d => ({ ...d.data(), docId: d.id }))));
    
    const qCage = query(collection(db, "cage_logs"), orderBy("date", "desc"));
    onSnapshot(qCage, (snap) => setCageLogs(snap.docs.map(d => ({ ...d.data(), docId: d.id }))));
    
    const qHandover = query(collection(db, "handover_logs"), orderBy("date", "desc"));
    onSnapshot(qHandover, (snap) => setHandoverLogs(snap.docs.map(d => ({ ...d.data(), docId: d.id }))));
    
    const qTelegram = query(collection(db, "telegram_outbox"), orderBy("date", "desc"));
    onSnapshot(qTelegram, (snap) => setTelegramLogs(snap.docs.map(d => ({ ...d.data(), docId: d.id }))));
    
    const vaultRef = doc(db, "system", "credit_vault");
    onSnapshot(vaultRef, (snap) => {
        if (snap.exists()) {
            setCreditVault(snap.data());
        } else {
            setDoc(vaultRef, { balance: 50000000, updatedAt: new Date().toISOString() });
        }
    });
  }, []);

  const getNextTxId = async () => {
    try {
      const ref = doc(db, "system", "counters");
      const snap = await getDoc(ref);
      let n = 100001;
      if (snap.exists()) { 
        n = snap.data().txCounter + 1;
        await updateDoc(ref, { txCounter: n }); 
      } else { 
        await setDoc(ref, { txCounter: 100001 });
      }
      return n;
    } catch (e) { return Date.now(); }
  };

  const addAuditLog = async (type, targetId, detail) => {
    await addDoc(collection(db, "audit_logs"), {
      date: new Date().toISOString(), staff: currentUser?.code || 'SYSTEM', type, targetId, detail
    });
  };

  const addCageLog = async (type, category, amount, remark, extraData = {}) => {
      const staffId = currentUser ? currentUser.code : 'SYSTEM';
      await addDoc(collection(db, "cage_logs"), {
          date: new Date().toISOString(),
          type, category, amount: parseFloat(amount) || 0, remark,
          staff: staffId, ...extraData 
      });
  };

  const addHandoverLog = async (data) => {
      await addDoc(collection(db, "handover_logs"), {
          ...data,
          date: new Date().toISOString(),
          recordedBy: currentUser ? currentUser.code : 'SYSTEM'
      });
  };

  const sendTelegram = async (agentCode, type, message) => {
      const agent = agents.find(a => a.code === agentCode);
      const chatId = agent?.telegramId || '';
      
      await addDoc(collection(db, "telegram_outbox"), {
          date: new Date().toISOString(),
          to: agentCode,
          chatId: chatId,
          type: type,
          message: message,
          status: chatId ? 'Sent' : 'Failed (No ID)', 
          sender: currentUser ? currentUser.code : 'SYSTEM'
      });

      if (sysConfig.telegramBotToken && chatId) {
          try {
              const url = `https://api.telegram.org/bot${sysConfig.telegramBotToken}/sendMessage`;
              const response = await fetch(url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ chat_id: chatId, text: message })
              });
              
              const result = await response.json();
              if (result.ok) {
                  console.log(`Telegram Sent to ${agentCode}:`, result);
              } else {
                  console.error(`Telegram Failed to ${agentCode}:`, result);
              }
          } catch (e) {
              console.error("Telegram API Error:", e);
          }
      } else {
          console.warn("Telegram Skipped: Missing Bot Token or Chat ID");
      }
  };

  const sendSms = async (agentCode, type, message) => {
      await sendTelegram(agentCode, type, message);
  };

  const addSmsTemplate = (title, content) => {
      const newTemplate = { id: Date.now(), title, content };
      setSmsTemplates([...smsTemplates, newTemplate]);
  };
  const deleteSmsTemplate = (id) => {
      setSmsTemplates(smsTemplates.filter(t => t.id !== id));
  };
  const updateFunds = (newFunds) => { setFunds(newFunds); };
  const addStaff = (staff) => { setStaffList([...staffList, { ...staff, id: Date.now() }]); };
  const removeStaff = (id) => { setStaffList(staffList.filter(s => s.id !== id)); };
  const updatePermissions = (newPermissions) => { setPermissions(newPermissions); };
  const updateCurrency = (currency) => { setGlobalCurrency(currency); }; 
  const updateSysConfig = (newConfig) => { setSysConfig(prev => ({ ...prev, ...newConfig })); };

  const processAgentTransaction = async (agentDocId, agentCode, type, amount, remark, gameId = null, forceNegative = false) => {
    const staffId = currentUser ? currentUser.code : 'SYSTEM';
    const agentRef = doc(db, "agents", agentDocId);
    const agentSnap = await getDoc(agentRef);
    if (!agentSnap.exists()) return false;
    
    const currentBalance = parseFloat(agentSnap.data().balance || 0);
    let newBalance = currentBalance;
    
    if (['Deposit', 'Return', 'Rolling Commission', 'Share Profit', 'Chips Out', 'Rebate'].includes(type)) {
        newBalance += amount;
    } else if (['Withdraw', 'Buy-in', 'Initial Buy-in', 'Add Buy-in', 'Credit Issue', 'Repay Loan', 'Expense Transfer'].includes(type)) {
        if (!forceNegative && currentBalance < amount) return false;
        newBalance -= amount;
    }

    await updateDoc(agentRef, { balance: newBalance });
    await addDoc(collection(db, "agent_transactions"), {
        date: new Date().toISOString(), agentCode, type, amount, balance: newBalance, remark, gameId, staff: staffId
    });
    
    const telegramMsg = `[Junket System]\nTransaction: ${type}\nAmount: ${amount.toLocaleString()}\nBalance: ${newBalance.toLocaleString()}\nRemark: ${remark}`;
    sendTelegram(agentCode, 'AUTO_REPORT', telegramMsg);
    
    return true;
  };

  const addAgent = async (newAgentData) => { 
      try {
          const newId = agents.length > 0 ? Math.max(...agents.map(a => a.id)) + 1 : 1; 
          await addDoc(collection(db, "agents"), { ...newAgentData, id: newId, status: 'Active', balance: 0, creator: currentUser?.code, createdAt: new Date().toISOString() });
          await addAuditLog('회원 생성', newAgentData.code, '신규 에이전트 등록 완료'); 
      } catch (error) {
          console.error("Add Agent Error:", error);
          throw error;
      }
  };
  
  const updateAgent = async (updatedData, manualLog) => { 
      if (!updatedData.docId) return;
      try {
          const agentRef = doc(db, "agents", updatedData.docId); 
          const changeLog = manualLog || '정보 업데이트'; 
          await updateDoc(agentRef, updatedData); 
          addAuditLog('회원 수정', updatedData.code, changeLog);
      } catch (error) {
          console.error("Update Agent Error:", error);
          throw error;
      }
  };
  
  const generateGameId = (type) => { 
      let p = 'A'; if (type === 'Share') p = 'B';
      if (type === 'Credit') p = 'C'; 
      const nums = games.map(g => parseInt(g.id.replace(/\D/g,'')) || 0);
      const max = nums.length > 0 ? Math.max(...nums) : 0; 
      return p + String(max + 1).padStart(5, '0'); 
  };
  
  const createGame = async (gameData) => { 
      const agent = agents.find(a => a.code === gameData.agentId);
      if (!agent) { alert("Agent Not Found"); return null; } 
      
      const initialAmount = parseFloat(String(gameData.buyIn).replace(/,/g, '')) || 0; 
      const newId = generateGameId(gameData.type);
      const remarkText = `Game Creation: ${gameData.type} (Game ID: ${newId})`; 
      
      if (gameData.type === 'Credit') { 
          if (currentUser?.type !== 'Master_Admin') { alert("권한이 없습니다 (Master Only)."); return null; } 
          const vaultRef = doc(db, "system", "credit_vault"); 
          const currentVaultBalance = parseFloat(creditVault.balance || 0);
          if (currentVaultBalance < initialAmount) { 
              if(!window.confirm("⚠️ 금고 잔액이 부족합니다. 그래도 진행하시겠습니까?")) return null;
          } 
          await updateDoc(vaultRef, { balance: currentVaultBalance - initialAmount }); 
          await addDoc(collection(db, "loans"), { loanId: `L-${newId}`, gameId: newId, agentId: agent.code, principal: initialAmount, repaidAmount: 0, status: 'Active', createdAt: new Date().toISOString(), creator: currentUser?.code });
      } else { 
          const success = await processAgentTransaction(agent.docId, agent.code, 'Initial Buy-in', initialAmount, remarkText, newId); 
          if (!success) { alert("에이전트 잔고가 부족합니다."); return null; } 
      } 
      
      const newGameData = { ...gameData, buyIn: initialAmount, id: newId, status: 'Active', balance: initialAmount, totalRolling: initialAmount, sumBuyIn: initialAmount, sumCashOut: 0, startedAt: new Date().toISOString(), creator: currentUser?.code };
      await addDoc(collection(db, "games"), newGameData); 
      
      const nextIdNum = await getNextTxId(); 
      const currentTxIdStr = `TX-${nextIdNum}`;
      const newTx = { id: Date.now(), txCode: currentTxIdStr, date: new Date().toISOString(), staff: currentUser?.code, gameId: newId, type: 'Initial Buy-in', amount: initialAmount, cashChips: 0, rollingChips: 0, remark: 'Initial Buy-in' };
      await addDoc(collection(db, "transactions"), newTx); 
      return newId; 
  };

  const addTransaction = async (gameId, txData) => { 
      const nextIdNum = await getNextTxId();
      const currentTxIdStr = `TX-${nextIdNum}`; 
      const cleanAmount = String(txData.amount).replace(/,/g, ''); 
      const finalAmount = parseFloat(cleanAmount) || 0;
      const targetGame = games.find(g => g.id === gameId); 
      if (!targetGame) return; 
      const agent = agents.find(a => a.code === targetGame.agentId);

      if (txData.type === 'Add Buy-in') { 
          if (targetGame.type === 'Credit') { 
              const vaultRef = doc(db, "system", "credit_vault");
              const currentVault = parseFloat(creditVault.balance || 0); 
              await updateDoc(vaultRef, { balance: currentVault - finalAmount });
              const loan = credits.find(c => c.gameId === gameId); 
              if (loan) { await updateDoc(doc(db, "loans", loan.docId), { principal: (parseFloat(loan.principal) || 0) + finalAmount }); } 
          } else { 
              const success = await processAgentTransaction(agent.docId, agent.code, 'Add Buy-in', finalAmount, `Game Add Buy-in (Game ID: ${gameId})`, gameId);
              if (!success) { alert("잔고 부족"); return; } 
          } 
      } 
      
      if (txData.type === 'Chips Out') {
           if (targetGame.type !== 'Credit') {
               await processAgentTransaction(agent.docId, agent.code, 'Chips Out', finalAmount, `Game Chips Out (Game ID: ${gameId})`, gameId);
           }
      }

      const newTx = { id: Date.now(), txCode: currentTxIdStr, date: new Date().toISOString(), staff: currentUser?.code, gameId: gameId, ...txData, amount: finalAmount };
      await addDoc(collection(db, "transactions"), newTx); 
      
      let newBalance = parseFloat(targetGame.balance || 0); 
      let newRolling = parseFloat(targetGame.totalRolling || 0);
      let newSumBuyIn = parseFloat(targetGame.sumBuyIn || 0); 
      let newSumCashOut = parseFloat(targetGame.sumCashOut || 0); 
      const rChips = parseFloat(String(txData.rollingChips).replace(/,/g, '')) || 0;

      if (['Buy-in', 'Initial Buy-in', 'Add Buy-in'].includes(txData.type)) { 
          newBalance += finalAmount; newRolling += finalAmount; newSumBuyIn += finalAmount;
      } else if (txData.type === 'Chips Out') {  
          newBalance -= finalAmount; newSumCashOut += finalAmount;
          if (rChips > 0) newRolling = Math.max(0, newRolling - rChips); 
      } else if (txData.type === 'Rolling') { 
          newRolling += finalAmount;
      } else if (txData.type === 'Settle') { 
          newBalance -= finalAmount; newSumCashOut += finalAmount;
          if (rChips > 0) newRolling = Math.max(0, newRolling - rChips);
          
          if (targetGame.type === 'Credit') { 
              const loan = credits.find(c => c.gameId === gameId);
              if (loan) { 
                  const principal = parseFloat(loan.principal || 0); 
                  const prevRepaid = parseFloat(loan.repaidAmount || 0);
                  const outstanding = principal - prevRepaid; 
                  const amountToVault = Math.min(finalAmount, outstanding); 
                  const amountToAgent = Math.max(0, finalAmount - outstanding);
                  
                  const vaultRef = doc(db, "system", "credit_vault"); 
                  const currentVault = parseFloat(creditVault.balance || 0); 
                  await updateDoc(vaultRef, { balance: currentVault + amountToVault });
                  
                  const newRepaidTotal = prevRepaid + amountToVault; 
                  const isFullyPaid = newRepaidTotal >= principal;
                  await updateDoc(doc(db, "loans", loan.docId), { repaidAmount: newRepaidTotal, status: isFullyPaid ? 'Settled' : 'Active', settledAt: isFullyPaid ? new Date().toISOString() : null });
                  
                  if (amountToAgent > 0) { 
                      await processAgentTransaction(agent.docId, agent.code, 'Deposit', amountToAgent, `Credit Game Profit (Game ID: ${gameId})`, gameId);
                  } 
                  
                  const totalRolling = newRolling; 
                  const commRate = parseFloat(targetGame.rollingComm || 0); 
                  const rollingCommAmount = Math.floor(totalRolling * (commRate / 100));
                  if (rollingCommAmount > 0) { 
                      const remainingDebt = principal - newRepaidTotal;
                      if (remainingDebt > 0) {
                          const rollToVault = Math.min(rollingCommAmount, remainingDebt);
                          const rollToAgent = rollingCommAmount - rollToVault;
                          
                          await updateDoc(vaultRef, { balance: (currentVault + amountToVault) + rollToVault });
                          await updateDoc(doc(db, "loans", loan.docId), { repaidAmount: newRepaidTotal + rollToVault, status: (newRepaidTotal + rollToVault >= principal) ? 'Settled' : 'Active', settledAt: (newRepaidTotal + rollToVault >= principal) ? new Date().toISOString() : null });
                          
                          if (rollToAgent > 0) await processAgentTransaction(agent.docId, agent.code, 'Rolling Commission', rollToAgent, `Credit Rolling (Partial Pay) - Game ID: ${gameId}`, gameId);
                      } else {
                          await processAgentTransaction(agent.docId, agent.code, 'Rolling Commission', rollingCommAmount, `Credit Rolling Commission (${commRate}%) - Game ID: ${gameId}`, gameId);
                      }

                      if (agent.upline) {
                          const uplineAgent = agents.find(a => a.code === agent.upline);
                          if (uplineAgent) {
                              const uplineRate = parseFloat(uplineAgent.rollingRate || 0);
                              const downlineRate = commRate;
                              if (uplineRate > downlineRate) {
                                  const gap = uplineRate - downlineRate;
                                  const rebateAmount = Math.floor(totalRolling * (gap / 100));
                                  if (rebateAmount > 0) {
                                      await processAgentTransaction(
                                          uplineAgent.docId, 
                                          uplineAgent.code, 
                                          'Rebate', 
                                          rebateAmount, 
                                          `Rebate from ${agent.code} (Gap: ${gap.toFixed(2)}%) - Game ID: ${gameId}`,
                                          gameId
                                      );
                                  }
                              }
                          }
                      }
                  } 
              } 
          } else { 
              await processAgentTransaction(agent.docId, agent.code, 'Return', finalAmount, `Settle Chip Return (Game ID: ${gameId})`, gameId);
              
              const totalRolling = newRolling; 
              const commRate = parseFloat(targetGame.rollingComm || 0); 
              const rollingCommAmount = Math.floor(totalRolling * (commRate / 100));
              
              if (rollingCommAmount > 0) { 
                  await processAgentTransaction(agent.docId, agent.code, 'Rolling Commission', rollingCommAmount, `Auto Commission (${commRate}%) - Game ID: ${gameId}`, gameId);
                  
                  if (agent.upline) {
                      const uplineAgent = agents.find(a => a.code === agent.upline);
                      if (uplineAgent) {
                          const uplineRate = parseFloat(uplineAgent.rollingRate || 0);
                          const downlineRate = commRate;
                          
                          if (uplineRate > downlineRate) {
                              const gap = uplineRate - downlineRate;
                              const rebateAmount = Math.floor(totalRolling * (gap / 100));
                              
                              if (rebateAmount > 0) {
                                  await processAgentTransaction(
                                      uplineAgent.docId, 
                                      uplineAgent.code, 
                                      'Rebate', 
                                      rebateAmount, 
                                      `Rebate from ${agent.code} (Gap: ${gap.toFixed(2)}%) - Game ID: ${gameId}`,
                                      gameId
                                  );
                              }
                          }
                      }
                  }
              } 
              
              if (targetGame.type === 'Share') { 
                  const netWinLoss = (newSumBuyIn - newSumCashOut) - rollingCommAmount;
                  if (netWinLoss > 0) { 
                      const shareProfitAmount = Math.floor(netWinLoss * (parseFloat(targetGame.shareRate || 0) / 100));
                      if (shareProfitAmount > 0) { 
                          await processAgentTransaction(agent.docId, agent.code, 'Share Profit', shareProfitAmount, `Auto Share Profit (${targetGame.shareRate}%) - Game ID: ${gameId}`, gameId);
                      } 
                  } 
              } 
          } 
      } 
      
      const gameRef = doc(db, "games", targetGame.docId);
      await updateDoc(gameRef, { balance: newBalance, totalRolling: newRolling, sumBuyIn: newSumBuyIn, sumCashOut: newSumCashOut }); 
  };

  const repayLoan = async (loanId, amount) => { 
      const loan = credits.find(c => c.loanId === loanId);
      if (!loan) return alert("Loan not found"); 
      const agent = agents.find(a => a.code === loan.agentId); 
      if (!agent) return alert("Agent not found");
      const success = await processAgentTransaction(agent.docId, agent.code, 'Repay Loan', amount, `Manual Repay Loan: ${loanId}`, loan.gameId);
      if (!success) { alert("에이전트 잔고가 부족하여 상환할 수 없습니다."); return false; } 
      
      const vaultRef = doc(db, "system", "credit_vault");
      const currentVault = parseFloat(creditVault.balance || 0); 
      await updateDoc(vaultRef, { balance: currentVault + amount }); 
      
      const prevRepaid = parseFloat(loan.repaidAmount || 0);
      const principal = parseFloat(loan.principal || 0); 
      const newRepaidTotal = prevRepaid + amount; 
      const isFullyPaid = newRepaidTotal >= principal;
      await updateDoc(doc(db, "loans", loan.docId), { repaidAmount: newRepaidTotal, status: isFullyPaid ? 'Settled' : 'Active', settledAt: isFullyPaid ? new Date().toISOString() : null });
      return true; 
  };
  
  const settleGameStatus = async (gameId) => { 
      const targetGame = games.find(g => g.id === gameId);
      if(targetGame) { 
          const gameRef = doc(db, "games", targetGame.docId); 
          const settler = currentUser ? currentUser.code : 'ADMIN';
          await updateDoc(gameRef, { status: 'Settled', endedAt: new Date().toISOString(), settledBy: settler }); 
      } 
  };
  
  const addSettlement = async (settlementData) => { await addDoc(collection(db, "settlements"), settlementData); };
  const updateGame = async (gameData) => { 
      const target = games.find(g => g.id === gameData.id);
      if (target) { const gameRef = doc(db, "games", target.docId); await updateDoc(gameRef, gameData); } 
  };
  
  const login = (id, password) => {
    if (id === 'MASTER_ADMIN' && password === 'a168168') {
      const user = { code: 'MASTER_ADMIN', type: 'Master_Admin', engSurname: 'MASTER', engGivenName: 'ADMIN' };
      setCurrentUser(user); localStorage.setItem('junket_user', JSON.stringify(user)); return true;
    }
    const staff = staffList.find(s => s.code === id && s.password === password);
    if(staff) {
        const user = { code: staff.code, type: staff.role, engSurname: staff.name, engGivenName: '' };
        setCurrentUser(user); localStorage.setItem('junket_user', JSON.stringify(user)); return true;
    }
    const user = agents.find(a => a.code === id && a.password === password);
    if (user) { setCurrentUser(user); localStorage.setItem('junket_user', JSON.stringify(user)); return true; }
    return false;
  };
  
  const logout = () => { setCurrentUser(null); localStorage.removeItem('junket_user'); };
  
  const resetSystem = async () => { 
      try {
        setLoading(true);
        const collections = ['agents', 'games', 'transactions', 'audit_logs', 'agent_transactions', 'loans', 'cage_logs', 'handover_logs', 'sms_outbox', 'settlements', 'telegram_outbox'];
        for (const colName of collections) {
            const q = query(collection(db, colName));
            const snapshot = await getDocs(q);
            const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);
        }

        await setDoc(doc(db, "system", "credit_vault"), { balance: 50000000, updatedAt: new Date().toISOString() });
        await setDoc(doc(db, "system", "counters"), { txCounter: 100001 });

        localStorage.clear();
        alert("시스템이 성공적으로 초기화되었습니다.");
        window.location.href = '/'; 
      } catch (error) {
          console.error("Reset Failed:", error);
          alert("초기화 실패: " + error.message);
      } finally {
          setLoading(false);
      }
  };

  useEffect(() => { const savedUser = localStorage.getItem('junket_user'); if (savedUser) setCurrentUser(JSON.parse(savedUser)); }, []);

  // ★ [핵심 수정] 권한별 하부 데이터 필터링 로직 개선
  const filteredData = useMemo(() => {
      // 1. 관리자 권한 (Master, Senior Manager, Manager, Staff) -> 전체 데이터 반환
      // 여기서 userType 체크를 강화하여 Senior Manager도 포함되도록 함
      const userType = currentUser?.type || '';
      const isAdmin = 
          ['Master_Admin', 'Manager', 'Staff'].includes(userType) || 
          userType.includes('Manager'); // 'Senior Manager' 등 포함

      if (!currentUser || isAdmin) {
          return { agents, games, transactions, agentTransactions }; 
      }

      // 2. 에이전트/그룹 -> 본인 및 하부(Downline)만 보기
      const myCode = currentUser.code;
      
      const getDownlines = (parentId) => {
          let list = [];
          const children = agents.filter(a => a.upline === parentId);
          list = [...list, ...children.map(c => c.code)];
          children.forEach(c => { list = [...list, ...getDownlines(c.code)]; });
          return list;
      };
      
      const myDownlines = getDownlines(myCode);
      const visibleCodes = [myCode, ...myDownlines]; // 본인 + 하부 코드 목록

      // 필터링 적용
      const visibleAgents = agents.filter(a => visibleCodes.includes(a.code));
      const visibleGames = games.filter(g => visibleCodes.includes(g.agentId));
      
      const visibleTransactions = {};
      Object.keys(transactions).forEach(gameId => {
          const game = games.find(g => g.id === gameId);
          // 게임이 내 라인(본인/하부)의 것일 때만 포함
          if (game && visibleCodes.includes(game.agentId)) {
              visibleTransactions[gameId] = transactions[gameId];
          }
      });
      
      const visibleAgentTxs = agentTransactions.filter(t => visibleCodes.includes(t.agentCode));

      return {
          agents: visibleAgents,
          games: visibleGames,
          transactions: visibleTransactions,
          agentTransactions: visibleAgentTxs
      };
  }, [currentUser, agents, games, transactions, agentTransactions]);

  return (
    <DataContext.Provider value={{ 
      currentUser, login, logout, 
      agents: filteredData.agents, // 필터링된 데이터 제공
      games: filteredData.games,
      transactions: filteredData.transactions,
      agentTransactions: filteredData.agentTransactions,
      
      auditLogs, addAuditLog, processAgentTransaction,
      credits, creditVault, 
      cageLogs, addCageLog, handoverLogs, addHandoverLog,
      smsLogs, sendSms: sendTelegram, smsTemplates, addSmsTemplate, deleteSmsTemplate,
      telegramLogs,
      funds, updateFunds, staffList, addStaff, removeStaff, permissions, updatePermissions,
      resetSystem, globalCurrency, updateCurrency,
      sysConfig, updateSysConfig,
      createGame, addTransaction, repayLoan, settleGameStatus, addSettlement, updateGame, loading,
      addAgent, updateAgent, 
      
      allAgents: agents 
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);